import { FC, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, Snackbar, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';

import CustomInput from '../../../../../common/CustomInput';
import CustomButton from '../../../../../common/CustomButton';
import ConfirmActionModal from '../../../../../common/ConfirmActionModal/ConfirmActionModal';
import SendReferralEmailModal from './SendReferralEmailModal';
import { EmailIcon } from '../../../../../components/Icons';
import { useBottomSuccessToast } from '../../../../../hooks/useBottomSuccessToast';
import {
  useCreateReferralLinkMutation,
  useGetReferralLinkQuery,
  useSendReferralLinkMutation,
} from '../../../../../store/services/referralApi';

import styles from './ReferralProgram.module.scss';

const ReferralProgram: FC = () => {
  const { data: referralLink, isLoading, isError, refetch } = useGetReferralLinkQuery();
  const [createReferralLink, { isLoading: isCreating }] = useCreateReferralLinkMutation();
  const [sendReferralLink, { isLoading: isSending }] = useSendReferralLinkMutation();
  const {
    show: showSuccessToast,
    onClose: closeSuccessToast,
    snackbarKey,
    open: toastOpen,
    message: toastMessage,
  } = useBottomSuccessToast();

  const [copySuccess, setCopySuccess] = useState(false);
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const referralUrl = referralLink?.url ?? '';
  const hasLink = Boolean(referralUrl);

  const handleGenerateLink = async () => {
    setCreateError(null);
    try {
      await createReferralLink().unwrap();
    } catch {
      setCreateError('Could not generate your referral link. Please try again.');
    }
  };

  const handleRegenerateConfirm = async () => {
    setCreateError(null);
    try {
      await createReferralLink().unwrap();
      setRegenerateModalOpen(false);
    } catch {
      setCreateError('Could not regenerate your referral link. Please try again.');
    }
  };

  const handleCopyLink = async () => {
    if (!referralUrl) return;

    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy referral link:', err);
    }
  };

  const handleSendReferralEmail = async (email: string) => {
    await sendReferralLink({ email }).unwrap();
    setSendEmailModalOpen(false);
    showSuccessToast('Referral link sent successfully');
  };

  return (
    <Box className={styles.referralProgram}>
      <Typography
        variant="h4Bold"
        className={styles.title}
      >
        Referral program
      </Typography>
      <Typography
        variant="h10"
        className={styles.description}
      >
        Share your personal referral link with friends.
      </Typography>

      {isLoading && (
        <Box className={styles.loading}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isLoading && isError && (
        <Box className={styles.errorState}>
          <Typography variant="h11">Could not load your referral link. Please try again.</Typography>
          <Typography
            variant="h10Bold"
            className={styles.retryBtn}
            onClick={() => refetch()}
          >
            Retry
          </Typography>
        </Box>
      )}

      {!isLoading && !isError && !hasLink && (
        <Box className={styles.generateSection}>
          <CustomButton
            variant="contained"
            onClick={handleGenerateLink}
            loading={isCreating}
            disabled={isCreating}
            customClass={styles.generateBtn}
          >
            Generate link
          </CustomButton>
          {createError && (
            <Typography
              variant="h11"
              className={styles.errorText}
            >
              {createError}
            </Typography>
          )}
        </Box>
      )}

      {!isLoading && !isError && hasLink && (
        <Box className={styles.linkRow}>
          <CustomInput
            inputName="referralUrl"
            inputLabel="Your referral link"
            value={referralUrl}
            onChange={() => {}}
            disabled
            customClass={styles.urlInput}
          />
          <Box className={styles.actions}>
            <Tooltip title={copySuccess ? 'Copied!' : 'Copy link'}>
              <span>
                <IconButton
                  onClick={handleCopyLink}
                  className={styles.actionBtn}
                  aria-label="Copy referral link"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Send via email">
              <span>
                <IconButton
                  onClick={() => setSendEmailModalOpen(true)}
                  className={styles.actionBtn}
                  aria-label="Send referral link via email"
                >
                  <EmailIcon
                    size={20}
                    color="#222B3A"
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Regenerate link">
              <span>
                <IconButton
                  onClick={() => setRegenerateModalOpen(true)}
                  className={styles.actionBtn}
                  aria-label="Regenerate referral link"
                  disabled={isCreating}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {createError && (
            <Typography
              variant="h11"
              className={styles.errorText}
            >
              {createError}
            </Typography>
          )}
        </Box>
      )}

      <SendReferralEmailModal
        open={sendEmailModalOpen}
        onClose={() => !isSending && setSendEmailModalOpen(false)}
        onSend={handleSendReferralEmail}
        isSending={isSending}
      />

      <ConfirmActionModal
        open={regenerateModalOpen}
        onClose={() => !isCreating && setRegenerateModalOpen(false)}
        onConfirm={handleRegenerateConfirm}
        title="Are you sure?"
        description="Do you really want to regenerate your referral link? Previous links will no longer be valid."
        confirmLabel="Regenerate"
        cancelLabel="Cancel"
        isConfirming={isCreating}
        confirmingLabel="Regenerating..."
      />

      <Snackbar
        key={snackbarKey}
        open={toastOpen}
        autoHideDuration={5000}
        onClose={closeSuccessToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSuccessToast}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferralProgram;
